// @archigraph IXnoTz4c
// @archigraph KxPcmCOQ
// Sign Up page for archigraph.ai

import { useState, useMemo, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { ErrorMessage } from '../components/ui/ErrorMessage';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function checkPassword(v: string) {
  return {
    minLength: v.length >= 8,
    hasUpper: /[A-Z]/.test(v),
    hasLower: /[a-z]/.test(v),
    hasNumber: /[0-9]/.test(v),
  };
}

function passwordStrength(v: string): 'weak' | 'medium' | 'strong' {
  const c = checkPassword(v);
  if (!c.minLength || !c.hasUpper || !c.hasLower || !c.hasNumber) return 'weak';
  if (v.length >= 12) return 'strong';
  return 'medium';
}

const strengthConfig = {
  weak: { width: 'w-1/3', color: 'bg-error', label: 'weak' },
  medium: { width: 'w-2/3', color: 'bg-warning', label: 'medium' },
  strong: { width: 'w-full', color: 'bg-primary', label: 'strong' },
} as const;

type Step = 'form' | 'verify' | 'done';

export function SignupPage() {
  const { signup, confirmSignup, resendCode, login, isLoading, error, clearError } = useAuthStore();

  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [cognitoUsername, setCognitoUsername] = useState('');
  const [appOpened, setAppOpened] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const checks = useMemo(() => checkPassword(password), [password]);
  const strength = useMemo(() => passwordStrength(password), [password]);

  const validateName = (v: string) => v.length < 2 ? 'Name must be at least 2 characters' : '';
  const validateEmail = (v: string) => !EMAIL_REGEX.test(v) ? 'Please enter a valid email' : '';
  const validatePassword = (v: string) => {
    const c = checkPassword(v);
    return (!c.minLength || !c.hasUpper || !c.hasLower || !c.hasNumber)
      ? 'Password must meet all requirements' : '';
  };

  const isFormValid = useMemo(() => {
    return !validateName(name) && !validateEmail(email) && !validatePassword(password) && termsAccepted && !isLoading;
  }, [name, email, password, termsAccepted, isLoading]);

  // @archigraph 9IdiKaTY
  // @archigraph triggers|ui.website.signup|interface.auth.signup|api
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setNameTouched(true);
    setEmailTouched(true);

    const nErr = validateName(name);
    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    setNameError(nErr);
    setEmailError(eErr);
    setPasswordError(pErr);

    if (nErr || eErr || pErr || !termsAccepted) return;

    clearError();
    try {
      const result = await signup(email, password, name);
      setCognitoUsername(result.username);
      setStep('verify');
    } catch {
      // Error is set in the store
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmSignup(cognitoUsername, verificationCode);
      // Log the user in so we can pass tokens to the app
      await login(email, password);
      const tokens = useAuthStore.getState().tokens;
      setStep('done');
      // Attempt to open the Electron app with auth tokens
      const params = new URLSearchParams({
        accessToken: tokens?.accessToken || '',
        idToken: tokens?.idToken || '',
        refreshToken: tokens?.refreshToken || '',
      });
      window.location.href = `archigraph://auth/callback?${params.toString()}`;
      // If the app opens, the browser stays on this page — that's fine
      // If it doesn't open, the user sees the fallback UI
      setTimeout(() => setAppOpened(true), 2000);
    } catch {
      // Error is set in the store
    }
  };

  const handleOpenApp = () => {
    const tokens = useAuthStore.getState().tokens;
    const params = new URLSearchParams({
      accessToken: tokens?.accessToken || '',
      idToken: tokens?.idToken || '',
      refreshToken: tokens?.refreshToken || '',
    });
    window.location.href = `archigraph://auth/callback?${params.toString()}`;
  };

  const sc = strengthConfig[strength];

  if (step === 'done') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <Container className="max-w-md">
          <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
            // account created
          </p>
          <h1 className="text-2xl font-bold text-text mb-2">You're in</h1>
          <p className="text-sm text-text-light mb-8">
            Opening ArchiGraph...
          </p>

          {appOpened && (
            <Card padding="lg" className="space-y-4">
              <p className="text-xs text-text-lighter leading-relaxed">
                Didn't open? Make sure ArchiGraph is installed, then try again.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" size="md" onClick={handleOpenApp}>
                  Open ArchiGraph
                </Button>
                <Link
                  to="/download"
                  className="inline-flex items-center justify-center rounded-none font-medium uppercase tracking-wider border border-border text-text-light hover:text-text hover:border-text-lighter px-6 py-2 text-xs transition-all"
                >
                  Download
                </Link>
              </div>
            </Card>
          )}
        </Container>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <Container className="max-w-md">
          <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
            // verify email
          </p>
          <h1 className="text-2xl font-bold text-text mb-1">Check your email</h1>
          <p className="text-xs text-text-lighter mb-8">
            We sent a verification code to <span className="text-text">{email}</span>
          </p>

          {error && (
            <ErrorMessage message={error} onDismiss={clearError} className="mb-6" />
          )}

          <Card padding="lg">
            <form onSubmit={handleVerify} noValidate className="space-y-5">
              <Input
                label="Verification code"
                type="text"
                autoComplete="one-time-code"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => { setVerificationCode(e.target.value); clearError(); }}
                disabled={isLoading}
                inputMode="numeric"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                disabled={verificationCode.length < 6 || isLoading}
                className="w-full"
              >
                Verify
              </Button>
            </form>

            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={async () => {
                  clearError();
                  try {
                    await resendCode(cognitoUsername);
                    setCodeSent(true);
                    setTimeout(() => setCodeSent(false), 5000);
                  } catch {
                    // Error shown via store
                  }
                }}
                disabled={isLoading || codeSent}
                className="text-xs text-text-lighter hover:text-primary transition-colors disabled:opacity-50"
              >
                {codeSent ? 'Code sent — check your email' : "Didn't get a code? Resend"}
              </button>
            </div>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12">
      <Container className="max-w-md">
        <p className="text-xs uppercase tracking-widest text-primary mb-4 font-bold">
          // create account
        </p>
        <h1 className="text-2xl font-bold text-text mb-1">Create your account</h1>
        <p className="text-xs text-text-lighter mb-8">Start designing architectures in minutes</p>

        {error && (
          <ErrorMessage message={error} onDismiss={clearError} className="mb-6" />
        )}

        <Card padding="lg">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <Input
              label="Name"
              type="text"
              autoComplete="name"
              placeholder="Your full name"
              value={name}
              onChange={(e) => { setName(e.target.value); if (nameTouched) setNameError(validateName(e.target.value)); clearError(); }}
              onBlur={() => { setNameTouched(true); setNameError(validateName(name)); }}
              error={nameTouched ? nameError : undefined}
              disabled={isLoading}
            />

            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailTouched) setEmailError(validateEmail(e.target.value)); clearError(); }}
              onBlur={() => { setEmailTouched(true); setEmailError(validateEmail(email)); }}
              error={emailTouched ? emailError : undefined}
              disabled={isLoading}
            />

            <div>
              <Input
                label="Password"
                type="password"
                autoComplete="new-password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(validatePassword(e.target.value)); clearError(); }}
                error={password.length > 0 ? passwordError : undefined}
                disabled={isLoading}
              />

              {password.length > 0 && (
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] uppercase tracking-widest text-text-lighter">strength</span>
                    <span className="text-[10px] uppercase tracking-widest text-text-lighter">{sc.label}</span>
                  </div>
                  <div className="w-full h-px bg-border">
                    <div className={`h-full transition-all duration-300 ${sc.width} ${sc.color}`} />
                  </div>
                </div>
              )}

              <ul className="mt-3 space-y-1" aria-label="Password requirements">
                {[
                  { met: checks.minLength, text: '8+ characters' },
                  { met: checks.hasUpper, text: '1 uppercase' },
                  { met: checks.hasLower, text: '1 lowercase' },
                  { met: checks.hasNumber, text: '1 number' },
                ].map((req) => (
                  <li key={req.text} className="flex items-center gap-2 text-[10px] text-text-lighter uppercase tracking-wider">
                    <span className={`w-1.5 h-1.5 ${req.met ? 'bg-primary' : 'bg-border'}`} />
                    <span className={req.met ? 'text-text-light' : ''}>{req.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={isLoading}
                className="mt-0.5 h-4 w-4 rounded-none border-border bg-bg text-primary focus:ring-primary accent-primary"
              />
              <span className="text-xs text-text-lighter leading-snug">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Terms</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-light">Privacy Policy</a>
              </span>
            </label>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isLoading}
              disabled={!isFormValid}
              className="w-full"
            >
              Sign Up
            </Button>

            <p className="text-xs text-text-lighter pt-2 border-t border-border">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:text-primary-light">Log in</Link>
            </p>
          </form>
        </Card>
      </Container>
    </div>
  );
}
